import { Switch, Redirect } from "react-router-dom";
import { PrivateRoute } from ".";
import { PermissionRoute } from "../Component/PermissionRoute";
import { Unauthorized } from "../Component/Unauthorized";
import { useSelector } from "react-redux";
import { lazy } from "react";
import { PERMISSIONS } from "../Utilis/permissions";
import { COMPANY_MODELS } from "../Hooks/company";

const Product = lazy(() => import("../Pages/Catalog/Product"));
const Home = lazy(() => import("../Pages/Home"));
const Reports = lazy(() => import("../Pages/Reports"));
const CompanyReport = lazy(() => import("../Pages/Reports/CompanyReport"));
const StoreCompare = lazy(() => import("../Pages/Reports/StoreCompare"));
const GrowthReport = lazy(() => import("../Pages/Reports/Growth"));
const ProductReport = lazy(() => import("../Pages/Reports/Products"));
const CustomerReport = lazy(() => import("../Pages/Reports/Customers"));
const OrderAnalytics = lazy(() => import("../Pages/Reports/OrderAnalytics"));
const RevenueReport = lazy(() => import("../Pages/Reports/Revenue"));
const ReportsDashboard = lazy(() => import("../Pages/Reports/Dashboard"));
const ExportLogs = lazy(() => import("../Pages/Reports/ExportLogs"));
const AboutPage = lazy(() => import("../Pages/StaticPages/About"));
const PrivacyPage = lazy(() => import("../Pages/StaticPages/Privacy/index"));
const TermsPage = lazy(() => import("../Pages/StaticPages/Terms/index"));
const FaqPage = lazy(() => import("../Pages/StaticPages/Faq/index"));
const InsuranceCompanies = lazy(() => import("../Pages/InsuranceCompanies"));
const InsuranceCoverage = lazy(() => import("../Pages/InsuranceCompanies/Coverage"));
const InsuranceOrder = lazy(() => import("../Pages/InsuranceOrder"));
const ShortLinks = lazy(() => import("../Pages/ShortLinks/index"));

const CompanySetting = lazy(() => import("../Pages/CompanySetting"));
const StoreDeliveryLocation = lazy(
  () => import("../Pages/CompanySetting/Stores/DeliveryLocation")
);
const ActivitiesList = lazy(() => import("../Pages/Activities"));
const ActivityManagement = lazy(
  () => import("../Pages/Activities/ActivityManagement")
);

const AddOrder = lazy(() => import("../Pages/Orders/AddOrder"));
const OrderDetails = lazy(
  () => import("../Pages/Orders/OrdersList/OrderDetails")
);
const OrderList = lazy(() => import("../Pages/Orders/OrdersList/index"));
const ActiveOrders = lazy(() => import("../Pages/Orders/ActiveOrders/index"));
const TrackOrder = lazy(
  () => import("../Pages/Orders/OrdersList/TrackOrder/index")
);

const OrderTracking = lazy(() => import("../Pages/Orders/Tracking/index"));

const AllCompanies = lazy(() => import("../Pages/AllCompanies/index"));
const EditCompany = lazy(
  () => import("../Pages/AllCompanies/CompanyForm/index")
);

const CreateCompany = lazy(
  () => import("../Pages/AllCompanies/CompanyForm/index")
);
const CompanyCustomizations = lazy(
  () => import("../Pages/CompanyCustomizations/index")
);
const CompanyCustomizationPresetDetail = lazy(
  () => import("../Pages/CompanyCustomizations/PresetDetail")
);
const SliderList = lazy(() => import("../Pages/E-Commerce/Slider/index"));
const CancelReasonsList = lazy(() => import("../Pages/E-Commerce/CancelReasons/index"));
const UncompletedOrderReasonsList = lazy(
  () => import("../Pages/E-Commerce/UncompletedOrderReasons/index")
);
const PrescriptionStatusesList = lazy(
  () => import("../Pages/E-Commerce/PrescriptionStatuses/index")
);
const PrescriptionRejectReasonsList = lazy(
  () => import("../Pages/E-Commerce/PrescriptionRejectReasons/index")
);
const PharmacistWorkspace = lazy(
  () => import("../Pages/Orders/PharmacistWorkspace/index")
);
const RefundsList = lazy(() => import("../Pages/Refunds/index"));
const RefundApprovalRulesList = lazy(
  () => import("../Pages/Refunds/ApprovalRules/index")
);
const FlashDealList = lazy(() => import("../Pages/E-Commerce/FlashDeal/index"));
const PromocodeList = lazy(() => import("../Pages/E-Commerce/Promocode/index"));
const HomeStaticContentList = lazy(
  () => import("../Pages/E-Commerce/HomeStaticContent/index")
);
const HomeLayoutList = lazy(() => import("../Pages/E-Commerce/HomeLayout/index"));
const BlogsList = lazy(() => import("../Pages/E-Commerce/Blogs/index"));
const VideosList = lazy(() => import("../Pages/E-Commerce/Videos/index"));
const SymptomsList = lazy(() => import("../Pages/E-Commerce/Symptoms/index"));

const BrandsList = lazy(() => import("../Pages/Catalog/Brands"));
const EditBrand = lazy(() => import("../Pages/Catalog/Brands/EditBrand/index"));
const ManufacturersList = lazy(() => import("../Pages/Catalog/Manufacturers"));
const EditManufacturer = lazy(() => import("../Pages/Catalog/Manufacturers/EditManufacturer/index"));

const ExtraList = lazy(() => import("../Pages/Catalog/Extra"));
const EditExtra = lazy(() => import("../Pages/Catalog/Extra/EditExtra/index"));

const CategoryList = lazy(() => import("../Pages/Catalog/Categories"));

const CreateCategory = lazy(
  () => import("../Pages/Catalog/Categories/CategoryForm")
);

const EditCategory = lazy(
  () => import("../Pages/Catalog/Categories/CategoryForm")
);

const AddSubCategory = lazy(
  () => import("../Pages/Catalog/Categories/CategoryForm")
);

const SubCategories = lazy(
  () => import("../Pages/Catalog/Categories/SubCategoriesPage")
);

const CreateProduct = lazy(
  () => import("../Pages/Catalog/Product/ProductForm")
);

const EditProduct = lazy(() => import("../Pages/Catalog/Product/ProductForm"));

const ProductAttributeList = lazy(() => import("../Pages/Catalog/ProductAttribute"));
const KeywordsList = lazy(() => import("../Pages/Catalog/Keywords"));
const ProductReviewsList = lazy(() => import("../Pages/Catalog/ProductReviews"));
const ReviewBannedWordsList = lazy(() => import("../Pages/Catalog/ReviewBannedWords"));

const SectionList = lazy(() => import("../Pages/E-Commerce/Sections"));

const FileManager = lazy(() => import("../Pages/Catalog/FileManager"));

const FailedProductImports = lazy(() => import("../Pages/Logs/FailedProductImports"));
const FailedOfferImports = lazy(() => import("../Pages/Logs/FailedOfferImports"));
const FailedCategoryImports = lazy(() => import("../Pages/Logs/FailedCategoryImports"));
const IntegrationMonitoring = lazy(() => import("../Pages/Logs/IntegrationMonitoring"));

const UsersList = lazy(() => import("../Pages/Users"));
const CustomizeUserColumns = lazy(() => import("../Pages/Users/CustomizeColumns"));

// Customers pages
const ManageCustomers = lazy(() => import("../Pages/Customers/ManageCustomers"));
const CustomerDetails = lazy(() => import("../Pages/Customers/CustomerDetails"));

// Offers pages
const OffersList = lazy(() => import("../Pages/Offers"));
const OffersGroup = lazy(() => import("../Pages/OffersGroup"));

const CreateOffer = lazy(() => import("../Pages/Offers/CreateOffer"));

// Location pages
const CountriesList = lazy(() => import("../Pages/Locations/Countries"));
const CitiesList = lazy(() => import("../Pages/Locations/Cities"));
const AreasList = lazy(() => import("../Pages/Locations/Areas"));
const RegionsList = lazy(() => import("../Pages/Locations/Regions"));

// Integration pages
const SMSIntegration = lazy(() => import("../Pages/Integrations/SMS"));
const PaymentGatewayIntegration = lazy(() => import("../Pages/Integrations/PaymentGateway"));

// DMS Settings pages
const AIDistribution = lazy(() => import("../Pages/DMSSettings/AIDistribution"));
const SkillsList = lazy(() => import("../Pages/DMSSettings/Skills"));
const ServiceTypesList = lazy(() => import("../Pages/DMSSettings/ServiceTypes"));
const TripTypesList = lazy(() => import("../Pages/DMSSettings/TripTypes"));
const ShoppersList = lazy(() => import("../Pages/DMSSettings/Shoppers"));
const ShopperOrderStatusList = lazy(() => import("../Pages/DMSSettings/ShopperOrderStatus"));
const CustodyPage = lazy(() => import("../Pages/DMSSettings/Custody"));

// DMS Purchasing pages
const PredictProducts = lazy(
  () => import("../Pages/DMSSettings/Purchasing/PredictProducts")
);
const PurchasingRequests = lazy(
  () => import("../Pages/DMSSettings/Purchasing/PurchasingRequests")
);
const AllPurchasingRequests = lazy(
  () => import("../Pages/DMSSettings/Purchasing/AllPurchasingRequests")
);

// DMS Reports pages
const DMSDashboard = lazy(() => import("../Pages/DMSReports/Dashboard"));
const Top10Shoppers = lazy(() => import("../Pages/DMSReports/Top10Shoppers"));
const DriverTracker = lazy(() => import("../Pages/DMSReports/DriverTracker"));
const AgentKPI = lazy(() => import("../Pages/DMSReports/AgentKPI"));
const Prediction = lazy(() => import("../Pages/DMSReports/Prediction"));

// Salla Orders pages
const SallaOrders = lazy(() => import("../Pages/SallaOrders"));
const SallaPurchasedOrderDetails = lazy(() => import("../Pages/SallaOrders/PurchasedOrderDetails"));
const Suppliers = lazy(() => import("../Pages/Suppliers"));
const PermissionsPage = lazy(() => import("../Pages/Permissions"));
const NotificationSoundsPage = lazy(
  () => import("../Pages/CompanySetting/NotificationSounds")
);
const PaymentSettingsPage = lazy(
  () => import("../Pages/CompanySetting/PaymentSettings")
);
const CustomerSegmentationPage = lazy(
  () => import("../Pages/CompanySetting/CustomerSegmentation")
);
const ExportReportsPage = lazy(
  () => import("../Pages/CompanySetting/ExportReports")
);
const EmailSettingsPage = lazy(
  () => import("../Pages/CompanySetting/EmailSettings")
);
const SMSTemplatesPage = lazy(
  () => import("../Pages/CompanySetting/SMSTemplates")
);
const PushNotificationsPage = lazy(
  () => import("../Pages/CompanySetting/PushNotifications")
);
const OrganizationPage = lazy(
  () => import("../Pages/CompanySetting/Organization")
);



export const RouterWrapper = () => {
  const isAuthenticated = localStorage.getItem("token");
  const { userInfo } = useSelector((state: any) => state.setReducer);

  return (
    <Switch>
      <PermissionRoute
        path="/unauthorized"
        exact={true}
        component={Unauthorized}
      />


      {/* Pages Routes */}
      <PermissionRoute
        path="/pages/about"
        exact={true}
        component={AboutPage}
        permissions={[PERMISSIONS.PAGES.VIEW]}
      />
      <PermissionRoute
        path="/pages/privacy"
        exact={true}
        component={PrivacyPage}
        permissions={[PERMISSIONS.PAGES.VIEW]}
      />
      <PermissionRoute
        path="/pages/terms"
        exact={true}
        component={TermsPage}
        permissions={[PERMISSIONS.PAGES.VIEW]}
      />
      <PermissionRoute
        path="/pages/faq"
        exact={true}
        component={FaqPage}
        permissions={[PERMISSIONS.PAGES.VIEW]}
      />
      <PermissionRoute
        path="/insurance-companies"
        exact={true}
        component={InsuranceCompanies}
        permissions={[PERMISSIONS.INSURANCE.VIEW]}
      />
      <PermissionRoute
        path="/insurance-companies/:id/coverage"
        exact={true}
        component={InsuranceCoverage}
        permissions={[PERMISSIONS.INSURANCE.VIEW]}
      />
      <PermissionRoute
        path="/insurance-order"
        exact={true}
        component={InsuranceOrder}
        permissions={[PERMISSIONS.INSURANCE_ORDER.VIEW]}
      />
      <PermissionRoute
        path="/short-links"
        exact={true}
        component={ShortLinks}
        permissions={[PERMISSIONS.SHORT_LINK.VIEW]}
      />

      {/* Catalog Routes */}
      <PermissionRoute
        path="/catalog/product"
        exact={true}
        component={Product}
        permissions={[PERMISSIONS.PRODUCT.VIEW]}
      />

      <PermissionRoute
        path="/add-new-product"
        exact={true}
        component={CreateProduct}
        permissions={[PERMISSIONS.PRODUCT.CREATE]}
      />

      <PermissionRoute
        path="/product/edit/:id"
        exact={true}
        component={EditProduct}
        permissions={[PERMISSIONS.PRODUCT.EDIT]}
      />

      <PermissionRoute
        path="/catalog/categories"
        exact={true}
        component={CategoryList}
        permissions={[PERMISSIONS.CATEGORY.VIEW]}
      />

      <PermissionRoute
        path="/add-new-category"
        exact={true}
        component={CreateCategory}
        permissions={[PERMISSIONS.CATEGORY.CREATE]}
      />

      <PermissionRoute
        path="/edit-category"
        exact={true}
        component={EditCategory}
        permissions={[PERMISSIONS.CATEGORY.EDIT]}
      />

      <PermissionRoute
        path="/sub-categories"
        exact={true}
        component={SubCategories}
        permissions={[PERMISSIONS.CATEGORY.VIEW]}
      />

      <PermissionRoute
        path="/add-sub-category"
        exact={true}
        component={AddSubCategory}
        permissions={[PERMISSIONS.CATEGORY.CREATE]}
      />

      <PermissionRoute
        path="/catalog/brands"
        exact={true}
        component={BrandsList}
        permissions={[PERMISSIONS.BRAND.VIEW]}
      />

      <PermissionRoute
        path="/edit-brand"
        exact={true}
        component={EditBrand}
        permissions={[PERMISSIONS.BRAND.EDIT]}
      />

      <PermissionRoute
        path="/catalog/manufacturers"
        exact={true}
        component={ManufacturersList}
        permissions={[PERMISSIONS.MANUFACTURER.VIEW]}
      />

      <PermissionRoute
        path="/edit-manufacturer"
        exact={true}
        component={EditManufacturer}
        permissions={[PERMISSIONS.MANUFACTURER.CREATE, PERMISSIONS.MANUFACTURER.EDIT]}
      />

      <PermissionRoute
        path="/catalog/extra"
        exact={true}
        component={ExtraList}
        permissions={[PERMISSIONS.EXTRA.VIEW]}
      />

      <PermissionRoute
        path="/edit-extra"
        exact={true}
        component={EditExtra}
        permissions={[PERMISSIONS.EXTRA.EDIT]}
      />

      <PermissionRoute
        path="/catalog/product-attributes"
        exact={true}
        component={ProductAttributeList}
        permissions={[PERMISSIONS.PRODUCT_ATTRIBUTE.VIEW]}
      />

      <PermissionRoute
        path="/catalog/keywords"
        exact={true}
        component={KeywordsList}
        permissions={[PERMISSIONS.KEYWORD.VIEW]}
      />

      <PermissionRoute
        path="/catalog/product-reviews"
        exact={true}
        component={ProductReviewsList}
        permissions={[PERMISSIONS.PRODUCT_REVIEW.VIEW]}
      />

      <PermissionRoute
        path="/catalog/review-banned-words"
        exact={true}
        component={ReviewBannedWordsList}
        permissions={[PERMISSIONS.PRODUCT_REVIEW.VIEW]}
      />


      <PermissionRoute
        path="/catalog/file-manager"
        exact={true}
        component={FileManager}
        permissions={[PERMISSIONS.FILE_MANAGER.VIEW]}
      />

      <PermissionRoute
        path="/logs/failed-product-imports"
        exact={true}
        component={FailedProductImports}
        permissions={[PERMISSIONS.PRODUCT.VIEW]}
      />

      <PermissionRoute
        path="/logs/failed-offer-imports"
        exact={true}
        component={FailedOfferImports}
        permissions={[PERMISSIONS.OFFER.VIEW]}
      />

      <PermissionRoute
        path="/logs/failed-category-imports"
        exact={true}
        component={FailedCategoryImports}
        permissions={[PERMISSIONS.CATEGORY.VIEW]}
      />

      {/* Integration logs are gated on report_view plus Awfar/company admin, because the
          stored payloads can contain customer names, phones and addresses. */}
      <PermissionRoute
        path="/logs/integration-monitoring"
        exact={true}
        component={IntegrationMonitoring}
        permissions={[PERMISSIONS.REPORTS.VIEW]}
        userTypes={[0, 1]}
      />

      {/* Order Routes */}
      <PermissionRoute
        path="/orders/add"
        exact={true}
        component={AddOrder}
        permissions={[PERMISSIONS.ORDER.CREATE]}
      />

      <PermissionRoute
        path="/orders/list"
        exact={true}
        component={OrderList}
        permissions={[PERMISSIONS.ORDER.VIEW]}
      />

      <PermissionRoute
        path="/orders/active"
        exact={true}
        component={ActiveOrders}
        permissions={[PERMISSIONS.ORDER.VIEW]}
      />

      <PermissionRoute
        path="/orders/pharmacist-review"
        exact={true}
        component={PharmacistWorkspace}
        permissions={[
          PERMISSIONS.PRESCRIPTION_REVIEW.VIEW,
          PERMISSIONS.PRESCRIPTION_REVIEW.REVIEW,
        ]}
        requiresModel={COMPANY_MODELS.PRESCRIPTION}
      />

      <PermissionRoute
        path="/orders/refunds"
        exact={true}
        component={RefundsList}
        permissions={[PERMISSIONS.REFUND.VIEW]}
        requiresModel={COMPANY_MODELS.REFUND}
      />

      <PermissionRoute
        path="/orders/refund-approval-rules"
        exact={true}
        component={RefundApprovalRulesList}
        permissions={[PERMISSIONS.REFUND_APPROVAL_RULE.VIEW]}
        requiresModel={COMPANY_MODELS.REFUND}
      />

      <PermissionRoute
        path="/orders/show/:id"
        exact={true}
        component={OrderDetails}
        permissions={[PERMISSIONS.ORDER.SHOW]}
      />

      <PermissionRoute
        path="/orders/track/:id"
        exact={true}
        component={TrackOrder}
        permissions={[PERMISSIONS.ORDER.VIEW_TRACKING]}
      />

      <PermissionRoute
        path="/orders/tracking"
        exact={true}
        component={OrderTracking}
        permissions={[PERMISSIONS.ORDER.MAP_TRACKING]}
      />

      {/* Company Routes */}
      <PermissionRoute
        path="/company-setting"
        exact={true}
        component={CompanySetting}
        permissions={[PERMISSIONS.COMPANY.SETTINGS]}
      />

      {/* Store delivery zones: the locations endpoints are gated on store_create,
          not store_edit. */}
      <PermissionRoute
        path="/company-setting/store/deliver_location/:id"
        exact={true}
        component={StoreDeliveryLocation}
        permissions={[PERMISSIONS.STORE_MANAGEMENT.CREATE]}
      />

      <PermissionRoute
        path="/notification-sounds"
        exact={true}
        component={NotificationSoundsPage}
        permissions={[PERMISSIONS.NOTIFICATION_SOUNDS.VIEW]}
      />

      <PermissionRoute
        path="/payment-settings"
        exact={true}
        component={PaymentSettingsPage}
        permissions={[PERMISSIONS.PAYMENT_SETTINGS.VIEW]}
      />

      <PermissionRoute
        path="/customer-segmentation"
        exact={true}
        component={CustomerSegmentationPage}
        permissions={[PERMISSIONS.CUSTOMER_SEGMENTATION.VIEW]}
      />

      <PermissionRoute
        path="/company-settings/export-reports"
        exact={true}
        component={ExportReportsPage}
        permissions={[PERMISSIONS.COMPANY.SETTINGS, PERMISSIONS.REPORTS.EXPORT]}
        requireAllPermissions={true}
      />

      <PermissionRoute
        path="/email-settings"
        exact={true}
        component={EmailSettingsPage}
        permissions={[PERMISSIONS.EMAIL_SETTINGS.VIEW]}
      />

      <PermissionRoute
        path="/company-settings/push-notifications"
        exact={true}
        component={PushNotificationsPage}
        permissions={[PERMISSIONS.PUSH_NOTIFICATIONS.VIEW, PERMISSIONS.PUSH_NOTIFICATIONS.SEND]}
      />

      <PermissionRoute
        path="/settings/organization"
        exact={true}
        component={OrganizationPage}
        permissions={[PERMISSIONS.ORGANIZATION.VIEW]}
      />

      <PermissionRoute
        path="/settings/sms-templates"
        exact={true}
        component={SMSTemplatesPage}
        permissions={[PERMISSIONS.SMS_TEMPLATE.VIEW]}
      />

      <PermissionRoute
        path="/all-companies"
        exact={true}
        component={AllCompanies}
        permissions={[PERMISSIONS.COMPANY.VIEW]}
      />

      <PermissionRoute
        path="/company-customizations"
        exact={true}
        component={CompanyCustomizations}
        permissions={[PERMISSIONS.COMPANY.VIEW]}
        userTypes={[0]}
      />

      <PermissionRoute
        path="/company-customizations/:presetId"
        exact={true}
        component={CompanyCustomizationPresetDetail}
        permissions={[PERMISSIONS.COMPANY.VIEW]}
        userTypes={[0]}
      />

      <PermissionRoute
        path="/edit-company"
        exact={true}
        component={EditCompany}
        permissions={[PERMISSIONS.COMPANY.EDIT]}
      />

      <PermissionRoute
        path="/create-company"
        exact={true}
        component={CreateCompany}
        permissions={[PERMISSIONS.COMPANY.CREATE]}
      />

      {/* Activity Routes */}
      <PermissionRoute
        path="/all-activities"
        exact={true}
        component={ActivitiesList}
        permissions={[PERMISSIONS.ACTIVITY.VIEW]}
      />

      <PermissionRoute
        path="/activity-management"
        exact={true}
        component={ActivityManagement}
        permissions={[
          PERMISSIONS.ACTIVITY_TYPE.VIEW,
          PERMISSIONS.ACTIVITY_STATUS.VIEW,
        ]}
      />

      {/* E-commerce Routes */}

      <PermissionRoute
        path="/e-commerce/sections"
        exact={true}
        component={SectionList}
        permissions={[PERMISSIONS.SECTION.VIEW]}
      />

      <PermissionRoute
        path="/e-commerce/sliders"
        exact={true}
        component={SliderList}
        permissions={[PERMISSIONS.SLIDER.VIEW]}
      />

      <PermissionRoute
        path="/e-commerce/cancel-reasons"
        exact={true}
        component={CancelReasonsList}
        permissions={[PERMISSIONS.CANCEL_REASON.VIEW]}
      />

      <PermissionRoute
        path="/e-commerce/uncompleted-order-reasons"
        exact={true}
        component={UncompletedOrderReasonsList}
        permissions={[
          PERMISSIONS.UNCOMPLETED_ORDER_REASON.VIEW,
          "manage_uncompleted_order_reason_view",
          "uncompleted_order_reason_view",
        ]}
      />

      <PermissionRoute
        path="/e-commerce/prescription-statuses"
        exact={true}
        component={PrescriptionStatusesList}
        permissions={[PERMISSIONS.PRESCRIPTION_STATUS.VIEW]}
        requiresModel={COMPANY_MODELS.PRESCRIPTION}
      />

      <PermissionRoute
        path="/e-commerce/prescription-reject-reasons"
        exact={true}
        component={PrescriptionRejectReasonsList}
        permissions={[PERMISSIONS.PRESCRIPTION_REJECT_REASON.VIEW]}
        requiresModel={COMPANY_MODELS.PRESCRIPTION}
      />

      <PermissionRoute
        path="/e-commerce/flash-deal"
        exact={true}
        component={FlashDealList}
        permissions={[PERMISSIONS.FLASH_DEAL.VIEW]}
      />

      <PermissionRoute
        path="/e-commerce/promocodes"
        exact={true}
        component={PromocodeList}
        permissions={[PERMISSIONS.PROMOCODE.VIEW]}
      />

      <PermissionRoute
        path="/e-commerce/home-static-content"
        exact={true}
        component={HomeStaticContentList}
        permissions={[PERMISSIONS.HOME_STATIC_CONTENT.VIEW]}
      />

      <PermissionRoute
        path="/e-commerce/home-layout"
        exact={true}
        component={HomeLayoutList}
        permissions={[PERMISSIONS.SECTION.VIEW]}
      />

      <PermissionRoute
        path="/e-commerce/blogs"
        exact={true}
        component={BlogsList}
        permissions={[PERMISSIONS.SECTION.VIEW]}
      />

      <PermissionRoute
        path="/e-commerce/videos"
        exact={true}
        component={VideosList}
        permissions={[PERMISSIONS.SECTION.VIEW]}
      />

      <PermissionRoute
        path="/e-commerce/symptoms"
        exact={true}
        component={SymptomsList}
        permissions={[PERMISSIONS.SECTION.VIEW]}
      />

      {/* User Routes */}
      <PermissionRoute
        path="/users"
        exact={true}
        component={UsersList}
        permissions={[PERMISSIONS.USER.VIEW]}
      />

      <PermissionRoute
        path="/users/customize-columns"
        exact={true}
        component={CustomizeUserColumns}
        permissions={[PERMISSIONS.CUSTOM_VIEWS.MANAGE]}
      />

      {/* Customer Routes */}
      <PermissionRoute
        path="/manage-customers"
        exact={true}
        component={ManageCustomers}
        permissions={[PERMISSIONS.CUSTOMER.MANAGE]}
      />

      <PermissionRoute
        path="/customers/:id/details"
        exact={true}
        component={CustomerDetails}
        permissions={[PERMISSIONS.CUSTOMER.MANAGE]}
      />

      {/* Offers Routes */}
      <PermissionRoute
        path="/offers"
        exact={true}
        component={OffersList}
        permissions={[PERMISSIONS.OFFER.VIEW]}
      />

      <PermissionRoute
        path="/offers-group"
        exact={true}
        component={OffersGroup}
        permissions={[PERMISSIONS.OFFER.VIEW]}
      />

      <PermissionRoute
        path="/offers/create"
        exact={true}
        component={CreateOffer}
        permissions={[PERMISSIONS.OFFER.CREATE]}
      />

      <PermissionRoute
        path="/offers/edit/:id"
        exact={true}
        component={CreateOffer}
        permissions={[PERMISSIONS.OFFER.EDIT]}
      />

      {/* Location Routes */}
      <PermissionRoute
        path="/locations/countries"
        exact={true}
        component={CountriesList}
        permissions={[PERMISSIONS.COUNTRY.VIEW]}
        userTypes={[0]}
      />

      <PermissionRoute
        path="/locations/cities"
        exact={true}
        component={CitiesList}
        permissions={[PERMISSIONS.CITY.VIEW]}
      />

      <PermissionRoute
        path="/locations/areas"
        exact={true}
        component={AreasList}
        permissions={[PERMISSIONS.AREA.VIEW]}
      />

      <PermissionRoute
        path="/locations/regions"
        exact={true}
        component={RegionsList}
        permissions={[PERMISSIONS.REGION.VIEW]}
      />

      {/* Integration Routes */}
      <PermissionRoute
        path="/integrations/sms"
        exact={true}
        component={SMSIntegration}
        permissions={[PERMISSIONS.COMPANY_CONFIGURATION.VIEW]}
      />

      <PermissionRoute
        path="/integrations/payment-gateway"
        exact={true}
        component={PaymentGatewayIntegration}
        permissions={[PERMISSIONS.COMPANY_CONFIGURATION.VIEW]}
      />

      {/* DMS Settings Routes — requiresDms: dms model only (even for admins) */}
      <PermissionRoute
        path="/dms-settings/ai-distribution"
        exact={true}
        component={AIDistribution}
        permissions={[PERMISSIONS.SHOPPER.SHOW]}
        requiresDms
      />

      <PermissionRoute
        path="/dms-settings/skills"
        exact={true}
        component={SkillsList}
        permissions={['manage_skill_view']}
        requiresDms
      />

      <PermissionRoute
        path="/dms-settings/service-types"
        exact={true}
        component={ServiceTypesList}
        permissions={['manage_service_type_view']}
        requiresDms
      />

      <PermissionRoute
        path="/dms-settings/trip-types"
        exact={true}
        component={TripTypesList}
        permissions={['manage_trip_type_view']}
        requiresDms
      />

      <PermissionRoute
        path="/dms-settings/shoppers"
        exact={true}
        component={ShoppersList}
        permissions={['shopper_view']}
        requiresDms
      />

      <PermissionRoute
        path="/dms-settings/shopper-order-status"
        exact={true}
        component={ShopperOrderStatusList}
        permissions={[PERMISSIONS.SHOPPER.SHOW]}
        requiresDms
      />

      <PermissionRoute
        path="/dms-settings/custody"
        exact={true}
        component={CustodyPage}
        permissions={['shoper_custody_view']}
        requiresDms
      />

      {/* Purchasing Routes */}
      <PermissionRoute
        path="/purchasing/predict-products"
        exact={true}
        component={PredictProducts}
        permissions={[PERMISSIONS.PURCHASING.PREDICT_PRODUCTS.VIEW]}
      />
      <PermissionRoute
        path="/purchasing/requests"
        exact={true}
        component={PurchasingRequests}
        permissions={[PERMISSIONS.PURCHASING.REQUESTS.VIEW]}
      />
      <PermissionRoute
        path="/purchasing/all-requests"
        exact={true}
        component={AllPurchasingRequests}
        permissions={[PERMISSIONS.PURCHASING.ALL_REQUESTS.VIEW]}
      />

      {/* DMS Reports Routes */}
      <PermissionRoute
        path="/dms-reports/dashboard"
        exact={true}
        component={DMSDashboard}
        permissions={[PERMISSIONS.DMS.DRIVER_TRACKER.VIEW]}
        requiresDms
      />
      <PermissionRoute
        path="/dms-reports/top-10-shoppers"
        exact={true}
        component={Top10Shoppers}
        permissions={[PERMISSIONS.DMS.DRIVER_TRACKER.VIEW]}
        requiresDms
      />
      <PermissionRoute
        path="/dms/driver-tracker"
        exact={true}
        component={DriverTracker}
        permissions={['dms_driver_tracker_view']}
        requiresDms
      />
      <PermissionRoute
        path="/dms/agent_kpi"
        exact={true}
        component={AgentKPI}
        permissions={['dms_agent_kpi_view']}
        requiresDms
      />
      <PermissionRoute
        path="/dms/prediction"
        exact={true}
        component={Prediction}
        permissions={['dms_prediction_view']}
        requiresDms
      />

      {/* Salla Orders Routes */}
      <PermissionRoute
        path="/salla-orders"
        exact={true}
        component={SallaOrders}
        permissions={[PERMISSIONS.SALLA_ORDERS.ALL, PERMISSIONS.SALLA_ORDERS.VIEW]}
      />

      <PermissionRoute
        path="/salla-orders/purchased"
        exact={true}
        component={SallaOrders}
        permissions={[PERMISSIONS.SALLA_ORDERS.PURCHASED_VIEW, PERMISSIONS.SALLA_ORDERS.VIEW]}
      />

      <PermissionRoute
        path="/salla-purchased-orders/:id"
        exact={true}
        component={SallaPurchasedOrderDetails}
        permissions={[PERMISSIONS.SALLA_ORDERS.PURCHASED_VIEW, PERMISSIONS.SALLA_ORDERS.VIEW]}
      />

      <PermissionRoute
        path="/suppliers"
        exact={true}
        component={Suppliers}
        permissions={[PERMISSIONS.SUPPLIER.VIEW]}
      />

      {/* Permissions / Roles / Models */}
      <PermissionRoute
        path="/permissions"
        exact={true}
        component={PermissionsPage}
        userTypes={[0, 1]}
        permissions={[
          PERMISSIONS.ROLE.VIEW,
          PERMISSIONS.PERMISSION.VIEW,
          PERMISSIONS.MODEL.VIEW,
        ]}
      />

      {/* Home Route */}
      <PrivateRoute
        path="/"
        exact={true}
        component={Home}
        isAuthenticated={!!isAuthenticated}
      />

      {/* Reports: specific path before /reports.
          Without company_settings, only Dashboard / Export / Companies are allowed. */}
      <PermissionRoute
        path="/reports/dashboard"
        exact={true}
        component={ReportsDashboard}
        permissions={[PERMISSIONS.REPORTS.VIEW]}
      />
      <PermissionRoute
        path="/reports/export-logs"
        exact={true}
        component={ExportLogs}
        permissions={[PERMISSIONS.REPORTS.VIEW, PERMISSIONS.REPORTS.EXPORT]}
        requireAllPermissions={true}
      />
      <PermissionRoute
        path="/reports/companies"
        exact={true}
        component={CompanyReport}
        permissions={[PERMISSIONS.REPORTS.VIEW]}
        userTypes={[0]}
      />
      <PermissionRoute
        path="/reports/store-compare"
        exact={true}
        component={StoreCompare}
        permissions={[PERMISSIONS.REPORTS.VIEW, PERMISSIONS.COMPANY.SETTINGS]}
        requireAllPermissions={true}
      />
      <PermissionRoute
        path="/reports/growth"
        exact={true}
        component={GrowthReport}
        permissions={[PERMISSIONS.REPORTS.VIEW, PERMISSIONS.COMPANY.SETTINGS]}
        requireAllPermissions={true}
      />
      <PermissionRoute
        path="/reports/products"
        exact={true}
        component={ProductReport}
        permissions={[PERMISSIONS.REPORTS.VIEW, PERMISSIONS.COMPANY.SETTINGS]}
        requireAllPermissions={true}
      />
      <PermissionRoute
        path="/reports/customers"
        exact={true}
        component={CustomerReport}
        permissions={[PERMISSIONS.REPORTS.VIEW, PERMISSIONS.COMPANY.SETTINGS]}
        requireAllPermissions={true}
      />
      <PermissionRoute
        path="/reports/order-analytics"
        exact={true}
        component={OrderAnalytics}
        permissions={[PERMISSIONS.REPORTS.VIEW, PERMISSIONS.COMPANY.SETTINGS]}
        requireAllPermissions={true}
      />
      <PermissionRoute
        path="/reports/revenue"
        exact={true}
        component={RevenueReport}
        permissions={[PERMISSIONS.REPORTS.VIEW, PERMISSIONS.COMPANY.SETTINGS]}
        requireAllPermissions={true}
      />
      <PermissionRoute
        path="/reports"
        exact={true}
        component={Reports}
        permissions={[PERMISSIONS.REPORTS.VIEW, PERMISSIONS.COMPANY.SETTINGS]}
        requireAllPermissions={true}
      />

      {/* Catch-all redirect to home */}
      <Redirect to="/" />
    </Switch>
  );
};

